import * as Dialog from '@radix-ui/react-dialog'
import { Outlet, useRouterState } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Toaster } from 'sonner'
import { useSystemTheme } from '@/lib/theme'
import { Brand, Sidebar, SidebarFooter, SidebarNav } from './Sidebar'
import { CommandPalette } from './CommandPalette'
import { Topbar } from './Topbar'

export function AppShell() {
  const routeKey = useRouterState({ select: (s) => s.location.pathname })
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const theme = useSystemTheme()
  const now = new Date()

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className="flex h-full">
      <Sidebar />

      <Dialog.Root open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-(--scrim) backdrop-blur-xs lg:hidden" />
          <Dialog.Content className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-surface shadow-pop animate-fade-in lg:hidden">
            <div className="flex items-center justify-between pr-3">
              <Brand />
              <Dialog.Close className="btn-ghost h-8 w-8 p-0" aria-label="Sluiten">
                <X className="h-4 w-4" />
              </Dialog.Close>
            </div>
            <SidebarNav onNavigate={() => setMobileNavOpen(false)} />
            <SidebarFooter onNavigate={() => setMobileNavOpen(false)} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          now={now}
          onOpenPalette={() => setPaletteOpen(true)}
          onOpenSidebar={() => setMobileNavOpen(true)}
        />
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div key={routeKey} className="mx-auto w-full max-w-6xl animate-fade-up px-4 py-8 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <Toaster
        position="bottom-right"
        theme={theme}
        toastOptions={{
          style: {
            borderRadius: '12px',
            border: '1px solid var(--hairline)',
            fontSize: '13px',
          },
        }}
      />
    </div>
  )
}
